import {
  WESTERN_PLANETS,
  type AspectHit,
  type AspectOrbConfig,
  type ChartPointId,
  type PlanetPosition,
} from '@astrally/shared';
import { computeCrossAspects, type ChartPoint } from './aspects.js';

/**
 * Synastry foundation: inter-chart aspects between two people's positions,
 * plus a bounded 0..1 harmony score used as a signal (never a verdict).
 */

const SYNASTRY_ORBS: AspectOrbConfig = {
  conjunction: 6,
  opposition: 6,
  trine: 5,
  square: 5,
  sextile: 4,
  quincunx: 2.5,
  'semi-sextile': 1.5,
  'semi-square': 1.5,
  sesquiquadrate: 1.5,
  luminaryBonus: 2,
};

export interface SynastryResult {
  aspects: AspectHit[];
  /** 0..1 harmony estimate from aspect mix. */
  score: number;
}

const HARMONY_WEIGHT: Record<AspectHit['type'], number> = {
  trine: 1,
  sextile: 0.8,
  'semi-sextile': 0.3,
  conjunction: 0.5, // depends on planets; treated mildly positive
  quincunx: -0.2, // adjustment rather than conflict
  'semi-square': -0.3,
  sesquiquadrate: -0.3,
  opposition: -0.4,
  square: -0.6,
};

/** Personal planets weigh more in relationship synastry. */
function pointWeight(id: ChartPointId): number {
  switch (id) {
    case 'sun':
    case 'moon':
      return 1.5;
    case 'venus':
    case 'mars':
      return 1.3;
    case 'mercury':
      return 1.0;
    default:
      return 0.6;
  }
}

export function computeSynastry(
  aPositions: PlanetPosition[],
  bPositions: PlanetPosition[],
): SynastryResult {
  const toPoints = (ps: PlanetPosition[]): ChartPoint[] =>
    ps
      .filter((p) => WESTERN_PLANETS.includes(p.planet))
      .map((p) => ({ id: p.planet as ChartPointId, lon: p.lonTropical }));

  const aspects = computeCrossAspects(toPoints(aPositions), toPoints(bPositions), SYNASTRY_ORBS);

  let raw = 0;
  let weightTotal = 0;
  for (const hit of aspects) {
    const w = pointWeight(hit.a) * pointWeight(hit.b);
    raw += HARMONY_WEIGHT[hit.type] * w;
    weightTotal += w;
  }
  // Map raw average in [-0.6, 1] to [0, 1]; empty aspect list = neutral 0.5.
  const avg = weightTotal > 0 ? raw / weightTotal : 0.2;
  const score = Math.min(1, Math.max(0, (avg + 0.6) / 1.6));

  return { aspects, score: Number(score.toFixed(3)) };
}
